import { useState } from "react";

import { Button } from "@/components/ui/button";

import { releaseStatusClass, releaseStatusLabel, statusClass, statusLabel } from "./qr-format";

// Small pieces the three QR tabs share. Every control here only changes what is
// shown, so each carries `data-nonmutating` and a view-only person keeps it.

/** The QR Codes tab's URL keys (carried as `f_q_code`, `f_q_place`, `f_q_status`, `f_q_view`, `f_q_release`). */
export const Q_CODE = "q_code";
export const Q_PLACE = "q_place";
export const Q_STATUS = "q_status";
export const Q_VIEW = "q_view";
export const Q_RELEASE = "q_release";
export const VIEW_RELEASES = "releases";

/** The QR Scans tab's own URL keys, kept apart from the QR Codes ones. */
export const S_GRAIN = "s_grain";
export const S_CODES = "s_codes";
export const S_PLACE = "s_place";
export const S_FUNNEL = "s_funnel";
export const S_CODE = "s_code";

export const numeric = { meta: { align: "right" as const } };

export const str = (value: unknown): string => (typeof value === "string" ? value : "");

export function StatusPill({ status }: { status: string }) {
  return <span className={`inline-flex items-center rounded-ks-sm px-2 py-0.5 text-xs font-medium ${statusClass(status)}`}>{statusLabel(status)}</span>;
}

export function ReleaseStatusPill({ status }: { status: string }) {
  return (
    <span className={`inline-flex items-center rounded-ks-sm px-2 py-0.5 text-xs font-medium ${releaseStatusClass(status)}`}>
      {releaseStatusLabel(status)}
    </span>
  );
}

export function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      size="sm"
      variant="outline"
      data-nonmutating
      className="text-foreground"
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(() => setCopied(true), () => setCopied(false));
      }}
    >
      {copied ? "Copied" : label}
    </Button>
  );
}

/**
 * A multi-select chip group ("any of"): each chip toggles one value in or out.
 * It lives here because the shared `FilterBar` chips are single-choice.
 */
export function MultiChips({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const toggle = (value: string) =>
    onChange(selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value]);
  return (
    <div className="grid gap-1.5">
      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{label}</span>
      <div role="group" aria-label={label} className="flex flex-wrap gap-1.5">
        {options.map((option) => {
          const active = selected.includes(option.value);
          return (
            <button
              key={option.value}
              type="button"
              data-nonmutating
              aria-pressed={active}
              onClick={() => toggle(option.value)}
              className={[
                "h-9 rounded-ks-sm border px-3.5 text-[13px] transition-colors",
                active ? "border-border bg-ks-purple-4 font-semibold text-ks-purple" : "border-border bg-ks-surface font-medium text-muted-foreground hover:bg-muted",
              ].join(" ")}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
